import { Fraction } from 'bi-fraction';
import { MIN_AMOUNT_PRELAUNCH_DEPOSIT } from '../config';
import { NATIVE_TOKEN_ADDRESS } from '../config/constants';
import { ChainLayer, } from '../types';
import { executeTx, getProvider } from '../utils';
const CRO_DECIMALS = 18;
const LCRO_DECIMALS = 18;
const FEES_FOR_APPROVE = 51548n;
const FEES_FOR_DEPOSIT = 94168n;
export class PrelaunchDepositCronosEvm {
    constructor(contract, lcro, providerOptions) {
        this.contract = contract;
        this.lcro = lcro;
        this.providerOptions = providerOptions;
    }
    async deposit(owner, tokenAddress, amount) {
        if (tokenAddress === NATIVE_TOKEN_ADDRESS) {
            return this.depositCro(amount);
        }
        return this.depositLcro(owner, amount);
    }
    async depositCro(amount) {
        const amountBigInt = amount.shl(CRO_DECIMALS).quotient;
        if (amountBigInt < MIN_AMOUNT_PRELAUNCH_DEPOSIT) {
            throw new Error(`depositCro value is too small, min is ${MIN_AMOUNT_PRELAUNCH_DEPOSIT}`);
        }
        return this.contract.depositNative({ value: amountBigInt });
    }
    async depositLcro(owner, amount) {
        const allowance = await this.lcro.allowance(owner, this.contract.getAddress());
        const amountBigInt = amount.shl(LCRO_DECIMALS).quotient;
        if (allowance < amountBigInt) {
            const tx = this.lcro.approve(this.contract.getAddress(), amountBigInt);
            await executeTx(tx);
        }
        return this.contract.depositLcro(amountBigInt);
    }
    /**
     * Returns an estimate of the gas to be spent for early depositing native CRO or CRC 20 LCRO tokens
     *
     * @param owner address of depositor
     * @param tokenAddress address of token to be deposited, might be NATIVE_TOKEN_ADDRESS for CRO
     * @param amount amount to be deposited
     * @returns estimated gas fee in CRO
     */
    async estimateGasDeposit(owner, tokenAddress, amount) {
        if (tokenAddress === NATIVE_TOKEN_ADDRESS) {
            return this.estimateGasDepositCro(owner, amount);
        }
        return this.estimateGasDepositLcro(owner, amount);
    }
    async estimateGasDepositCro(owner, amount) {
        if (amount.lt(new Fraction(MIN_AMOUNT_PRELAUNCH_DEPOSIT)))
            return null;
        const amountBigInt = amount.shl(CRO_DECIMALS).quotient;
        const provider = getProvider(this.providerOptions, ChainLayer.L1);
        const balance = await provider.getBalance(owner);
        if (balance < amountBigInt)
            return null;
        const [gasEstimate, { gasPrice }] = await Promise.all([
            this.contract.depositNative.estimateGas({
                value: amountBigInt,
                from: owner,
            }),
            getProvider(this.providerOptions, ChainLayer.L1).getFeeData(),
        ]);
        return new Fraction(gasEstimate * (gasPrice ?? 0n)).shr(CRO_DECIMALS);
    }
    async estimateGasDepositLcro(owner, amount) {
        const amountBigInt = amount.shl(LCRO_DECIMALS).quotient;
        const [allowance, { gasPrice }] = await Promise.all([
            this.lcro.allowance(owner, this.contract.getAddress()),
            getProvider(this.providerOptions, ChainLayer.L1).getFeeData(),
        ]);
        if (!gasPrice)
            return null;
        // these values need to be hardcoded because we cannot auto-estimate gas of deposit if approval is missing
        // we have to update it in case SC changes
        const resultBigInt = (FEES_FOR_DEPOSIT + (allowance < amountBigInt ? FEES_FOR_APPROVE : 0n)) *
            gasPrice;
        return new Fraction(resultBigInt).shr(CRO_DECIMALS);
    }
}
